import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Search, Camera, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { UserData } from "./LoginPage";
import { Movimiento } from "@/modules/CajaBancos/MovimientosSection";
import { usuariosAPI, cajaBancosAPI, type Usuario, type TipoMovimiento } from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";
import { postSessionBroadcast } from "@/lib/sessionBroadcast";
import { toast } from "sonner";
import { PvPredeterminadoList } from "./PvPredeterminadoList";
import { assignedPuntosVentaFromUser, pickDefaultPuntoVenta } from "@/lib/pvAsignado";
import { mergeSessionUser, persistSessionUser } from "@/lib/sessionUser";

interface PerfilPageProps {
  user: UserData;
  movimientos: Movimiento[];
  onBack: () => void;
  onUserUpdated?: (user: UserData) => void;
}

export function PerfilPage({ user, movimientos, onBack, onUserUpdated }: PerfilPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("todos");
  const [tipoFilter, setTipoFilter] = useState("todos");
  const [showReconfirmDialog, setShowReconfirmDialog] = useState(true);
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [customAvatarUrl, setCustomAvatarUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Avatar upload modal state
  const [usuarioData, setUsuarioData] = useState<Usuario | null>(null);
  const [tiposDefinidos, setTiposDefinidos] = useState<TipoMovimiento[]>([]);
  const [savingPvDefault, setSavingPvDefault] = useState(false);
  const [pvDefaultCodigo, setPvDefaultCodigo] = useState<string | null>(
    () =>
      (user.puntoVentaDefault || user.punto_venta_default || pickDefaultPuntoVenta(user)?.codigo || null),
  );
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarValidation, setAvatarValidation] = useState<{
    fileName: string;
    fileSize: string;
    fileType: string;
    isValidType: boolean;
    isValidSize: boolean;
    status: 'validating' | 'uploading' | 'success' | 'error';
    errorMessage?: string;
  } | null>(null);
  const userAvatarUrl = user.avatar_url || user.avatarUrl || null;

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show modal with validation
    const isValidType = file.type.startsWith('image/');
    const isValidSize = file.size <= 5 * 1024 * 1024;

    setAvatarValidation({
      fileName: file.name,
      fileSize: formatFileSize(file.size),
      fileType: file.type || 'Desconocido',
      isValidType,
      isValidSize,
      status: 'validating'
    });
    setShowAvatarModal(true);

    // Check validations
    if (!isValidType) {
      setAvatarValidation(prev => prev ? {
        ...prev,
        status: 'error',
        errorMessage: 'El archivo no es una imagen válida'
      } : null);
      return;
    }

    if (!isValidSize) {
      setAvatarValidation(prev => prev ? {
        ...prev,
        status: 'error',
        errorMessage: 'La imagen no debe superar los 5MB'
      } : null);
      return;
    }

    // Start upload
    setAvatarValidation(prev => prev ? { ...prev, status: 'uploading' } : null);
    setIsUploadingAvatar(true);

    try {
      const { usuariosAPI, getMediaUrl } = await import('@/lib/api');
      const result = await usuariosAPI.uploadAvatar(user.username, file);
      // console.log('Avatar upload result:', result);

      // Backend uses CamelCaseJSONRenderer, so check both avatar_url and avatarUrl
      const avatarUrl = result.avatar_url || (result as any).avatarUrl;

      if (result.success && avatarUrl) {
        // Use getMediaUrl to construct full URL with correct port
        setCustomAvatarUrl(getMediaUrl(avatarUrl));
        dataRefresh.emit("userAvatar");
        postSessionBroadcast({
          type: "avatar:updated",
          userId: user.id,
          codigo: user.codigo,
          username: user.username,
        });
        setAvatarValidation(prev => prev ? { ...prev, status: 'success' } : null);
        // Auto-close after success
        setTimeout(() => {
          setShowAvatarModal(false);
          setAvatarValidation(null);
        }, 1500);
      } else {
        setAvatarValidation(prev => prev ? {
          ...prev,
          status: 'error',
          errorMessage: 'Error al guardar el avatar en el servidor'
        } : null);
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      setAvatarValidation(prev => prev ? {
        ...prev,
        status: 'error',
        errorMessage: 'Error de conexión al subir la imagen'
      } : null);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Load saved avatar from backend on mount; currentUser revalidado tiene prioridad si trae avatar.
  React.useEffect(() => {
    const loadAvatar = async () => {
      try {
        if (userAvatarUrl) {
          const { getMediaUrl } = await import('@/lib/api');
          setCustomAvatarUrl(getMediaUrl(userAvatarUrl));
          return;
        }

        const { usuariosAPI, getMediaUrl } = await import('@/lib/api');
        const result = await usuariosAPI.getAvatar(user.username);
        // Handle both camelCase and snake_case
        const avatarUrl = result.avatar_url || (result as any).avatarUrl;
        if (avatarUrl) {
          setCustomAvatarUrl(getMediaUrl(avatarUrl));
        }
      } catch (err) {
        console.error('Failed to load avatar:', err);
      }
    };
    loadAvatar();
  }, [user.username, userAvatarUrl]);

  // Fetch usuario and tipos from API (replaces mock data)
  useEffect(() => {
    const load = async () => {
      try {
        const [users, tipos] = await Promise.all([
          usuariosAPI.list(),
          cajaBancosAPI.listTiposMovimiento()
        ]);
        const found = users.find((u: Usuario) => u.username === user.username);
        setUsuarioData(found ?? null);
        const fromList = found?.puntoVentaDefault || found?.punto_venta_default;
        if (fromList) setPvDefaultCodigo(String(fromList));
        setTiposDefinidos(tipos);
      } catch (e) {
        console.error("PerfilPage: Error loading usuario/tipos:", e);
      }
    };
    load();
  }, [user.username]);

  // Helper to resolve label using API tipos + fallback
  const getTipoMovimientoLabel = (codigoOrId: string | number) => {
    if (typeof codigoOrId === 'number') {
      const tipo = tiposDefinidos.find(t => t.id === codigoOrId);
      return tipo ? tipo.nombre : String(codigoOrId);
    }
    const tipo = tiposDefinidos.find(t => t.codigo === codigoOrId);
    return tipo ? tipo.nombre : codigoOrId;
  };

  const handleReconfirm = async () => {
    if (!password.trim()) {
      toast.error("Debe ingresar su contraseña");
      return;
    }
    if (verifyingPassword) return;
    setVerifyingPassword(true);
    try {
      await usuariosAPI.verifyPassword(password);
      setIsAuthenticated(true);
      setShowReconfirmDialog(false);
      setPassword("");
      toast.success("Autenticación confirmada");
    } catch {
      toast.error("Contraseña incorrecta");
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleCancelReconfirm = () => {
    onBack();
  };

  // Filter movimientos by current user (API returns 'usuario' FK id)
  const userMovimientos = movimientos.filter(
    mov => (mov.usuario ?? mov.usuarioId) === user.id
  );


  // Apply filters
  const filteredMovimientos = userMovimientos.filter(mov => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const tipoNombre = mov.tipoMovimientoNombre ?? mov.tipo_movimiento_nombre ?? getTipoMovimientoLabel(mov.tipoMovimiento ?? mov.tipo_movimiento ?? '');
    const matchesSearch =
      mov.codigo?.toLowerCase().includes(searchLower) ||
      tipoNombre.toLowerCase().includes(searchLower) ||
      (mov.observaciones && mov.observaciones.toLowerCase().includes(searchLower));

    if (!matchesSearch) return false;

    // Tipo filter (tipoFilter is codigo; compare with mov's tipo nombre)
    if (tipoFilter !== "todos") {
      const filterLabel = getTipoMovimientoLabel(tipoFilter);
      if (tipoNombre !== filterLabel) return false;
    }

    // Date filter
    const movDate = new Date(mov.fecha);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateFilter) {
      case "hoy": {
        const movDateStart = new Date(movDate);
        movDateStart.setHours(0, 0, 0, 0);
        return movDateStart.getTime() === today.getTime();
      }
      case "esta-semana": {
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        return movDate >= weekAgo;
      }
      case "este-mes":
        return movDate.getMonth() === today.getMonth() &&
          movDate.getFullYear() === today.getFullYear();
      default:
        return true;
    }
  });

  const assignedPvs = assignedPuntosVentaFromUser(user);
  const effectiveDefault =
    pvDefaultCodigo || pickDefaultPuntoVenta(user, assignedPvs)?.codigo || null;

  const applyUserPayload = (fresh: Usuario) => {
    const mergedUser = mergeSessionUser(user, fresh) ?? { ...user, ...fresh };
    const nextDefault =
      mergedUser.puntoVentaDefault ||
      mergedUser.punto_venta_default ||
      pickDefaultPuntoVenta(mergedUser)?.codigo ||
      null;
    setPvDefaultCodigo(nextDefault);
    const merged: UserData = {
      ...mergedUser,
      puntoVentaDefault: nextDefault,
    };
    persistSessionUser(merged);
    onUserUpdated?.(merged);
    postSessionBroadcast({
      type: "user:updated",
      userId: merged.id,
      codigo: merged.codigo,
      username: merged.username,
    });
  };

  const handleSetPredeterminado = async (codigo: string) => {
    if (codigo === effectiveDefault || savingPvDefault) return;
    setSavingPvDefault(true);
    try {
      const fresh = await usuariosAPI.updatePuntoVentaDefault(codigo);
      applyUserPayload(fresh);
      toast.success("Punto de venta predeterminado actualizado");
    } catch (err) {
      console.error(err);
      toast.error(
        err instanceof Error ? err.message : "No se pudo cambiar el Predeterminado",
      );
    } finally {
      setSavingPvDefault(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarUrl = (username: string) => {
    // Use custom avatar if uploaded, otherwise use dicebear
    if (customAvatarUrl) return customAvatarUrl;
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR');
  };

  // Get unique tipos for filter based on all defined tipos (not only user movimientos)
  const tiposMovimiento = tiposDefinidos.map(t => t.codigo);

  return (
    <div className="min-h-screen bg-background">
      {/* Reconfirmation Dialog */}
      <Dialog open={showReconfirmDialog} onOpenChange={(open) => !open && handleCancelReconfirm()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Acceso</DialogTitle>
            <DialogDescription>
              Por seguridad, debe reconfirmar su contraseña para acceder a su perfil.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleReconfirm();
                  }
                }}
                placeholder="Ingrese su contraseña"
                autoFocus
                disabled={verifyingPassword}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelReconfirm}>
              Cancelar
            </Button>
            <Button onClick={handleReconfirm} disabled={verifyingPassword}>
              {verifyingPassword ? "Confirmando…" : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avatar Upload Modal */}
      <Dialog open={showAvatarModal} onOpenChange={(open) => !open && setShowAvatarModal(false)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Subiendo Avatar</DialogTitle>
            <DialogDescription>
              Validando y subiendo imagen de perfil
            </DialogDescription>
          </DialogHeader>
          {avatarValidation && (
            <div className="space-y-4 py-4">
              {/* Validation Status */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {avatarValidation.isValidType ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span>Tipo de archivo válido (imagen)</span>
                </div>
                <div className="flex items-center gap-2">
                  {avatarValidation.isValidSize ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span>Tamaño menor a 5MB</span>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-center gap-2 py-4">
                {avatarValidation.status === 'validating' && (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                    <span className="text-blue-500">Validando...</span>
                  </>
                )}
                {avatarValidation.status === 'uploading' && (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                    <span className="text-blue-500">Subiendo avatar...</span>
                  </>
                )}
                {avatarValidation.status === 'success' && (
                  <>
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <span className="text-green-500 font-medium">¡Avatar actualizado!</span>
                  </>
                )}
                {avatarValidation.status === 'error' && (
                  <div className="text-center">
                    <XCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                    <span className="text-red-500">{avatarValidation.errorMessage}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAvatarModal(false);
                setAvatarValidation(null);
              }}
            >
              {avatarValidation?.status === 'error' ? 'Cerrar' : 'Cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Content - Show message when not authenticated */}
      {!isAuthenticated && !showReconfirmDialog && (
        <div className="max-w-7xl mx-auto p-8">
          <div className="text-center py-12 text-muted-foreground">
            <p>Debe confirmar su contraseña para acceder a su perfil</p>
          </div>
        </div>
      )}

      {/* Profile Content - Only shown when authenticated */}
      {isAuthenticated && (
        <div className="max-w-7xl mx-auto p-8 space-y-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
          </div>

          {/* User Info Card */}
          <div className="border border-input rounded-lg p-6 bg-muted/30">
            <div className="flex items-center gap-6">
              {/* Avatar with hover overlay for upload */}
              <div
                className="relative group cursor-pointer"
                onClick={handleAvatarClick}
              >
                <Avatar className="h-24 w-24">
                  <AvatarImage src={getAvatarUrl(user.username)} alt={user.username} />
                  <AvatarFallback className="text-2xl">{getInitials(user.username)}</AvatarFallback>
                </Avatar>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="h-8 w-8 text-white" />
                </div>
                {/* Loading overlay */}
                {isUploadingAvatar && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
                {/* Hidden file input */}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Row 1 */}
                <div>
                  <p className="text-sm text-muted-foreground">Usuario</p>
                  <p className="font-semibold capitalize">{user.username}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nombre Completo</p>
                  <p className="font-semibold">
                    {usuarioData ? `${usuarioData.nombre} ${usuarioData.apellido}` : `${user.nombre || ''} ${user.apellido || ''}`.trim() || user.username}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-semibold">{user.email}</p>
                </div>
                {/* Row 2 */}
                <div>
                  <p className="text-sm text-muted-foreground">Teléfono</p>
                  <p className="font-semibold">{usuarioData?.telefono || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rol</p>
                  <p className="font-semibold capitalize">{user.rol}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Movimientos</p>
                  <p className="font-semibold text-lg text-blue-600">{userMovimientos.length}</p>
                </div>
              </div>
            </div>
          </div>

          {assignedPvs.length > 0 ? (
            <div className="border border-input rounded-lg p-6 bg-muted/30 space-y-3">
              <div>
                <h3 className="text-lg font-semibold">Puntos de venta</h3>
                <p className="text-sm text-muted-foreground">
                  Marcá el Predeterminado. Facturas, presupuestos y remitos de
                  ventas nuevas usan ese punto de venta y su sucursal. Los demás
                  siguen asignados.
                </p>
              </div>
              <PvPredeterminadoList
                items={assignedPvs.map((pv) => ({
                  codigo: pv.codigo,
                  nombre: pv.nombre || pv.codigo,
                  sucursalLabel: pv.sucursal_nombre || "",
                }))}
                defaultCodigo={effectiveDefault}
                onDefaultChange={handleSetPredeterminado}
                radioName="perfil-pv-predeterminado"
              />
              {savingPvDefault ? (
                <p className="text-xs text-muted-foreground">Guardando…</p>
              ) : null}
            </div>
          ) : null}

          {/* Movimientos Section */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Mis movimientos</h3>

            {/* Filters */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar por ID, tipo u observaciones..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={tipoFilter} onValueChange={setTipoFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los tipos</SelectItem>
                  {tiposMovimiento.map(tipo => (
                    <SelectItem key={tipo} value={tipo}>
                      {getTipoMovimientoLabel(tipo)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas las fechas</SelectItem>
                  <SelectItem value="hoy">Hoy</SelectItem>
                  <SelectItem value="esta-semana">Esta Semana</SelectItem>
                  <SelectItem value="este-mes">Este Mes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Movimientos Table */}
            {filteredMovimientos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border border-input rounded-lg">
                {searchQuery || dateFilter !== "todos" || tipoFilter !== "todos"
                  ? "No se encontraron movimientos con los filtros aplicados"
                  : "No has registrado movimientos aún"}
              </div>
            ) : (
              <div className="border border-input rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium">ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Tipo de Movimiento</th>
                      <th className="text-right py-3 px-4 text-sm font-medium">Importe</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMovimientos.map((mov) => (
                      <tr key={mov.id} className="border-t border-input hover:bg-muted/50">
                        <td className="py-3 px-4 text-sm font-mono">{mov.id}</td>
                        <td className="py-3 px-4 text-sm">{formatDate(mov.fecha)}</td>
                        <td className="py-3 px-4 text-sm">
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {(mov.tipoMovimientoNombre ?? mov.tipo_movimiento_nombre) || getTipoMovimientoLabel(mov.tipoMovimiento ?? mov.tipo_movimiento ?? '')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-semibold text-red-600">
                          ${mov.importe.toLocaleString('es-AR')}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {mov.observaciones || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted border-t-2 border-input">
                    <tr>
                      <td colSpan={3} className="py-3 px-4 text-sm font-semibold text-right">
                        Total (Filtrado):
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-bold text-lg text-red-600">
                        ${filteredMovimientos.reduce((sum, m) => sum + m.importe, 0).toLocaleString('es-AR')}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}